
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import Cookies from 'js-cookie';
import { useEffect, useState } from "react";

interface NavLink {
  name: string;
  href: string;
  dropdown?: { name: string; href: string; }[];
}

interface MobileNavigationProps {
  navLinks: NavLink[];
  mobileMenuOpen: boolean;
  activeDropdown: string | null;
  setActiveDropdown: (name: string | null) => void;
}

const MobileNavigation = ({
  navLinks,
  mobileMenuOpen,
  activeDropdown,
  setActiveDropdown
}: MobileNavigationProps) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = Cookies.get('auth_token');
      const isDemo = localStorage.getItem('isDemo') === 'true';
      setIsAuthenticated(!!(token && !isDemo));
    };

    checkAuth();
    // Re-check on focus to handle login in another tab
    const handleFocus = () => checkAuth();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, [location]); // Re-check on route change

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0, transition: { duration: 0.2 } },
    visible: { opacity: 1, height: "auto", transition: { duration: 0.3 } }
  };

  const toggleDropdown = (name: string) => {
    if (activeDropdown === name) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(name);
    }
  };

  return (
    <AnimatePresence>
      {mobileMenuOpen && (
        <motion.nav 
          className="md:hidden bg-dineai-blue/95 backdrop-blur-md border-t border-white/10"
          variants={mobileMenuVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <div className="px-2 pt-2 pb-6 space-y-1 sm:px-3">
            {navLinks.map((link, index) => (
              <div key={index} className="py-1">
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(link.name)}
                      className={`w-full flex items-center justify-between py-2 px-3 rounded-md text-base font-medium ${
                        activeDropdown === link.name ? "text-dineai-yellow" : "text-[#43acff]"
                      }`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <AnimatePresence>
                      {activeDropdown === link.name && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="pl-4 overflow-hidden"
                        >
                          {link.dropdown.map((item, idx) => (
                            <Link
                              key={idx}
                              to={item.href}
                              className="block py-2 px-3 rounded-md text-sm text-[#43acff]/80 hover:text-dineai-yellow"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={link.href}
                    className={`block py-2 px-3 rounded-md text-base font-medium ${
                      location.pathname === link.href ? "text-dineai-yellow" : "text-[#43acff] hover:text-dineai-yellow"
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4 flex flex-col space-y-3">
              <Link to={isAuthenticated ? "/restaurant-dashboard" : "/restaurant-login"} className="w-full">
                <Button
                  variant="outline"
                  className="w-full border-[#43acff]/50 text-[#43acff] hover:bg-[#43acff]/10"
                >
                  <User className="mr-1 w-4 h-4" />
                  {isAuthenticated ? "DASHBOARD" : "LOGIN"}
                </Button>
              </Link>
              <Link to="/restaurant-menu-search" className="w-full">
                <Button 
                  className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 font-semibold"
                >
                  SIGN UP NOW
                </Button>
              </Link>
            </div>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default MobileNavigation;
